import { useVisitorData } from "@fingerprint/react";
import { useState, useEffect, useCallback } from "react";

export const useClickHandler = (onSingleClick, onDoubleClick, delay = 300) => {
  const [clickTimeout, setClickTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const handleClick = useCallback(() => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      if (onDoubleClick) onDoubleClick();
    } else {
      const timeout = setTimeout(() => {
        setClickTimeout(null);
        if (onSingleClick) onSingleClick();
      }, delay);
      setClickTimeout(timeout);
    }
  }, [clickTimeout, onSingleClick, onDoubleClick, delay]);

  useEffect(() => {
    return () => {
      if (clickTimeout) clearTimeout(clickTimeout);
    };
  }, [clickTimeout]);

  return handleClick;
};

export function formatTime(ms: number): string {
  if (ms <= 0) return "few secs";

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad2 = (n: number) => n.toString().padStart(2, "0");

  if (hours > 0) return `${hours}:${pad2(minutes)}:${pad2(seconds)} hrs`;
  if (minutes > 0) return `${minutes}:${pad2(seconds)} mins`;
  return `0:${pad2(seconds)} secs`;
}

export function useCountdown(
  targetDate: Date | string | number | null,
  triggerFn?: () => void,
) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!targetDate) return () => {};
    const timer = setInterval(() => {
      setRemaining(new Date(targetDate).getTime() - Date.now());
    }, 1000);
    setRemaining(new Date(targetDate).getTime() - Date.now());

    return () => clearInterval(timer);
  }, [targetDate]);

  if (!targetDate) return null;

  const formattedTime = formatTime(remaining);
  if (formattedTime === "few secs" && triggerFn) {
    triggerFn();
  }
  return formattedTime;
}

// Returns a function that identifies the visitor and resolves to the
// Fingerprint event id, or null when the agent is unavailable (ad blockers,
// network failures). Callers must tolerate null rather than block the user.
export function useFingerprintEventId() {
  const { getData } = useVisitorData({ immediate: false });

  return useCallback(async () => {
    try {
      const { event_id: eventId } = await getData();
      return eventId ?? null;
    } catch (e) {
      console.error("Fingerprint identification failed:", e);
      return null;
    }
  }, [getData]);
}
